import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, catchError, finalize, map, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  CurrencyCode,
  CurrencyDisplayState,
  ExchangeRateQuoteResponse,
  RestaurantCurrencyResponse,
  SUPPORTED_CURRENCIES
} from '../models/currency';

const PREFERENCE_KEY = 'bravum.preferredCurrency';

const INITIAL_STATE: CurrencyDisplayState = {
  restaurantId: null,
  baseCurrency: null,
  selectedCurrency: null,
  requestedCurrency: null,
  rate: 1,
  loading: false,
  isStale: false,
  updatedAtUtc: null,
  nextUpdateAtUtc: null,
  source: null,
  attributionUrl: null,
  error: null
};

@Injectable({ providedIn: 'root' })
export class CurrencyService {
  private readonly baseUrl = environment.url_api_v2 + 'api/v2/currency/restaurants/';
  private readonly stateSubject = new BehaviorSubject<CurrencyDisplayState>(INITIAL_STATE);
  private readonly quoteCache = new Map<string, { quote: ExchangeRateQuoteResponse; validUntil: number }>();
  private readonly inFlightQuotes = new Map<string, Observable<ExchangeRateQuoteResponse>>();
  private requestVersion = 0;

  readonly state$ = this.stateSubject.asObservable();

  constructor(private readonly http: HttpClient) { }

  get snapshot(): CurrencyDisplayState {
    return this.stateSubject.value;
  }

  initialize(restaurantId: number): Observable<CurrencyDisplayState> {
    const version = ++this.requestVersion;
    this.stateSubject.next({ ...INITIAL_STATE, restaurantId, loading: true });

    return this.http.get<RestaurantCurrencyResponse>(`${this.baseUrl}${restaurantId}`).pipe(
      switchMap(info => {
        const base = this.normalize(info.baseCurrency);
        if (!base) return throwError(() => new Error('La moneda base recibida no está soportada.'));

        const preferred = this.readPreference();
        const target = preferred && info.supportedCurrencies.includes(preferred) ? preferred : base;
        const baseState = this.baseState(restaurantId, base, target);
        if (version === this.requestVersion) this.stateSubject.next(baseState);
        if (target === base) return of(baseState);
        return this.loadQuote(restaurantId, base, target, version).pipe(
          catchError(error => {
            const fallback = {
              ...this.baseState(restaurantId, base, base),
              error: this.errorMessage(error)
            };
            if (version === this.requestVersion) this.stateSubject.next(fallback);
            return of(fallback);
          })
        );
      }),
      catchError(error => {
        const state: CurrencyDisplayState = {
          ...INITIAL_STATE,
          restaurantId,
          error: this.errorMessage(error)
        };
        if (version === this.requestVersion) this.stateSubject.next(state);
        return of(state);
      })
    );
  }

  select(currency: string): Observable<CurrencyDisplayState> {
    const target = this.normalize(currency);
    const current = this.snapshot;
    if (!target || !current.restaurantId || !current.baseCurrency)
      return of(current);

    this.writePreference(target);
    const version = ++this.requestVersion;
    if (target === current.baseCurrency) {
      const state = this.baseState(current.restaurantId, current.baseCurrency, target);
      this.stateSubject.next(state);
      return of(state);
    }

    this.stateSubject.next({ ...current, requestedCurrency: target, loading: true, error: null });
    return this.loadQuote(current.restaurantId, current.baseCurrency, target, version).pipe(
      catchError(error => {
        const state = {
          ...this.baseState(current.restaurantId!, current.baseCurrency!, current.baseCurrency!),
          error: this.errorMessage(error)
        };
        if (version === this.requestVersion) this.stateSubject.next(state);
        return of(state);
      })
    );
  }

  convert(originalAmount: number | string | null | undefined, state = this.snapshot): number {
    const amount = Number(originalAmount);
    if (!Number.isFinite(amount)) return 0;
    const rate = Number(state.rate);
    return amount * (Number.isFinite(rate) && rate > 0 ? rate : 1);
  }

  private loadQuote(
    restaurantId: number,
    base: CurrencyCode,
    target: CurrencyCode,
    version: number
  ): Observable<CurrencyDisplayState> {
    return this.getQuote(restaurantId, base, target).pipe(
      map(quote => this.quoteState(quote)),
      tap(state => {
        if (version === this.requestVersion) this.stateSubject.next(state);
      })
    );
  }

  private getQuote(
    restaurantId: number,
    base: CurrencyCode,
    target: CurrencyCode
  ): Observable<ExchangeRateQuoteResponse> {
    const key = `${restaurantId}:${base}:${target}`;
    const cached = this.quoteCache.get(key);
    if (cached && Date.now() < cached.validUntil) return of(cached.quote);

    const active = this.inFlightQuotes.get(key);
    if (active) return active;

    const request = this.http
      .get<ExchangeRateQuoteResponse>(`${this.baseUrl}${restaurantId}/quote/${target}`)
      .pipe(
        tap(quote => {
          const providerExpiry = Date.parse(quote.nextUpdateAtUtc);
          const staleRetry = Date.now() + 5 * 60 * 1000;
          this.quoteCache.set(key, {
            quote,
            validUntil: quote.isStale || !Number.isFinite(providerExpiry) ? staleRetry : Math.max(Date.now(), providerExpiry)
          });
        }),
        finalize(() => this.inFlightQuotes.delete(key)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    this.inFlightQuotes.set(key, request);
    return request;
  }

  private baseState(restaurantId: number, base: CurrencyCode, requested: CurrencyCode): CurrencyDisplayState {
    return {
      ...INITIAL_STATE,
      restaurantId,
      baseCurrency: base,
      selectedCurrency: base,
      requestedCurrency: requested,
      rate: 1,
      loading: requested !== base
    };
  }

  private quoteState(quote: ExchangeRateQuoteResponse): CurrencyDisplayState {
    return {
      restaurantId: quote.restaurantId,
      baseCurrency: quote.baseCurrency,
      selectedCurrency: quote.targetCurrency,
      requestedCurrency: quote.targetCurrency,
      rate: quote.rate,
      loading: false,
      isStale: quote.isStale,
      updatedAtUtc: quote.updatedAtUtc,
      nextUpdateAtUtc: quote.nextUpdateAtUtc,
      source: quote.source,
      attributionUrl: quote.attributionUrl,
      error: null
    };
  }

  private normalize(value: string | null | undefined): CurrencyCode | null {
    const normalized = value?.trim().toUpperCase();
    return SUPPORTED_CURRENCIES.includes(normalized as CurrencyCode) ? normalized as CurrencyCode : null;
  }

  private readPreference(): CurrencyCode | null {
    if (typeof localStorage === 'undefined') return null;
    return this.normalize(localStorage.getItem(PREFERENCE_KEY));
  }

  private writePreference(currency: CurrencyCode): void {
    if (typeof localStorage !== 'undefined') localStorage.setItem(PREFERENCE_KEY, currency);
  }

  private errorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse)
      return error.error?.error || 'La conversión no está disponible. Se muestran los precios en la moneda base.';
    if (error instanceof Error) return error.message;
    return 'La conversión no está disponible. Se muestran los precios en la moneda base.';
  }
}
